import { Config } from 'config';

import { Deferred, resolve } from 'gear';

import { forEach } from 'lodash';

import { Point, Document, FigureConditions_Mixin } from 'cdl';

import { View3D } from './view3d';

import { Plugin,
         DocumentView_Mixin,
         SelectedFigures_Mixin,
         InteractiveView_Mixin } from '../view';

import { Doc3D_Model_Mixin } from './doc3d-model-mixin';

import { TextureView } from './texture-view';

import { parseGeometry } from './three-util';

import { Object3D, EmptyObject3D } from './object3d';

import { Tracker3D } from './move3d';

export const StaticCanvas3D = View3D.extend( DocumentView_Mixin     )
                                    .extend( SelectedFigures_Mixin  )
                                    .extend( FigureConditions_Mixin )
                                    .extend( Doc3D_Model_Mixin      ).extend({

  typeName: 'StaticCanvas3D',

  Properties: {

    document: { type: 'Document', def: null, owned: false, set( v ) { this._setDoc(v); } },

    textureView: { type: 'TextureView', def: null, owned: false, set( v ) { this._setTextureView(v); } },

    // [TODO]
    documentFilter: { type: 'function', def: null }, // Used by load$ by id
  },

  // [TODO]
  // backSideDarkFactor
  // backSideColor

  get model() {
    return this.doc.model3D;
  },

  get modelData() {
    return this.doc.modelData3D;
  },

  get textureSize() {
    return this.textureView.textureSize;
  },
  set textureSize( textureSize_ ) {
    this.textureView.textureSize = textureSize_;
  },

  isIddle() {
    return this.callParent() && this.modelData && !this._transformMode;
  },

  insertViewPlugins( plugins ) {

    plugins.insert( 0, [
      Plugin.PluginsLayer(),
      Plugin.FigureAnnotations(), // [TODO] Let users add this on demand

      Plugin.Tools()
    ]);

    this.callParent(plugins);
  },

  annotate(/* ...params */) {
    return this.figureAnnotations.annotate(...arguments);
  },
  unannotate(/* ...params */) {
    return this.figureAnnotations.unannotate(...arguments);
  },
  clearAnnotations(/* ...params */) {
    return this.figureAnnotations.clearAnnotations(...arguments);
  },


  add( figures, config ) {
    const selector = ( config && config.layer ) || this.destinationLayerSelector;
    if( selector ) {
      this.setActiveLayerBy(selector);
    }
    return this.callParent(figures,config);
  },

  setActiveLayerBy( selector ) {
    const { doc } = this;
    let layer = doc.activePage.layerBy(selector);
    if( layer ) {
      this.activeLayer = layer;
    }
    return this;
  },


  figureView( figure ) {
    return this.textureView.figureView(figure);
  },

  get figureViews() {
    return this.textureView.figureViews;
  },

  figureTextureTypes() {
    return this.textureView.figureTextureTypes();
  },

  asFastOffset( offset ) {
    return this.textureView.asFastOffset(offset);
  },

  docPoint( event, docIsland ) {
    const modelPoint = this.modelPoint(event);
    return modelPoint && this.modelToDoc(modelPoint,docIsland);
  },

  modelPoint( event ) {
    const uv = this.uvPoint( event );
    return uv && this.uvToModel(uv);
  },

  uvPoint( event ) {
    return this.viewToUV( this.viewPoint(event) );
  },

  viewToUV( viewPoint ) {
    const { camera, width, height } = this;
    const uv = this.object3D.uvFromViewPoint( camera, viewPoint, width, height );
    this.computeViewToTextureScale(); // [TODO] We should move this to a better place
    return uv;
  },

  viewDeltaToDoc( delta ) {
    return this.textureView.textureDeltaToDoc(this.viewDeltaToTexture(delta));
  },

  computeViewToTextureScale( viewPoint ) {
    if( ! this._viewToTextureScale ) {
      const { camera, width, height } = this;
      if( ! viewPoint ) {
        viewPoint = new Point(width/2,height/2); // Stable... always the same on each model load
      }
      const uv = this.object3D.uvFromViewPoint( camera, viewPoint, width, height );
      if( uv ) {
        const dist = 5;
        const testPoints = [
          viewPoint.translated_(    0, dist),
          viewPoint.translated_(    0,-dist),
          viewPoint.translated_( dist,    0),
          viewPoint.translated_(-dist,    0)
        ];
        const scales = [];
        forEach( testPoints, point => {
          const uv_close = this.object3D.uvFromViewPoint( camera, point, width, height );
          if( uv_close ) {
            scales.push( uv_close.minus(uv).norm() / dist );
          }
        });
        const uv_scale = Math.min(...scales); // [TODO] Use median ?
        const scale = uv_scale * this.textureSize; // * 1.25;

        const { controls } = this;
        const c = camera.position.clone().sub(controls.target).length();
        this._viewToTextureScale = {
          scale,
          dist: c,
          width, height
        };
      }

      this.redraw('tools','plugins');
    }
  },

  postResize() {
    this.callParent();
    if( ! this.textureView._previewingResize ) {
      this._viewToTextureScale = null;
      this.computeViewToTextureScale();
    }
    if (Config.keepLowerLeftCornerAsOrigin) {
      this.center = this.viewCenter;
    }
  },

  resetToOrigin() {
    this.zoom = 1;
    this.center = this.viewCenter;
  },

  viewDeltaToTexture( viewDelta ) {

    const { camera, controls, textureView } = this;
    const c = camera.position.clone().sub(controls.target).length();

    const s = this._viewToTextureScale;
    if( s ) {

      const rs =  viewDelta * s.scale * ( this.textureView.width / this.textureSize ) *
                  Math.sqrt((c+0.1)/s.dist); // Account for zooming

      // if( viewDelta === 1 ) { console.log(rs+' / '+c+' / '+s.dist+' / '+this.textureView.width+' / '+s.scale); }
      return rs;
    }

    const rse = viewDelta * 3 * (textureView.width/this.textureSize) * Math.sqrt((c+0.1)/200) ; //this.controls.zoom;

    // if( viewDelta === 1 ) { console.log('E: '+ rse+' / '+c+' / '+this.textureView.width); }
    return rse;
  },

  // Useful for default place where to add figures
  get frontDocIsland() {
    // [TODO] Use raytracing
    return this.doc.topDocIsland; // this.doc.topDocIslandFor( this.model.modelIslands[0] );
  },

  // [TODO] The coupling between Canvas3D and its TextureView is now quite complex
  // This is mainly to let plugins talk with the textureView or the canvas3D as if
  // their will be the same thing. It may be better at the end to leave the plugins
  // initialized with the Canvas3D, and forward the drawing instead of the way it
  // is done now.
  redraw() {
    this.textureView.redraw(...arguments);
    this.callParent(...arguments);
  },

  get textureZoom() {
    return this.textureView.zoom;
  },

  // Load by id or set document, the promise finished when the 3D model is shown
  load$( doc ) {

    this.fireEvent('loading',this);

    if( typeof doc === 'string' ) {

      return Document.load$(doc) .then( loadedDoc =>{
      return resolve( this._documentFilter ? this._documentFilter( loadedDoc ) : true ) .then( () => {

      return this._setDoc(loadedDoc);
      }); });
    }

    return this._setDoc(doc);
  },

  onLoaded() {
    this._readyDeferred.resolve(this.doc);
    this._readyDeferred = null;
    this.fireEvent('loaded',this);
  },

  get controlsEnabled() {
    return this.controls.enabled;
  },
  set controlsEnabled( state ) {
    const { controls } = this;
    if( state === false && this._controlsPanning ) {
      controls.onMouseUp();
      this._controlsPanning = false;
    }
    controls.enabled = state;
  },
  setOrbitMode() {
    this.controls.mouseButtons = { ORBIT: THREE.MOUSE.LEFT, ZOOM: THREE.MOUSE.MIDDLE, PAN: THREE.MOUSE.RIGHT };
  },
  setPanMode() {
    this.controls.mouseButtons = { ORBIT: THREE.MOUSE.RIGHT, ZOOM: THREE.MOUSE.MIDDLE, PAN: THREE.MOUSE.LEFT };
  },
  get isOrbitMode(){return this.controls.mouseButtons.ORBIT == THREE.MOUSE.LEFT},
  get isPanMode(){return this.controls.mouseButtons.PAN == THREE.MOUSE.LEFT},
  init( config = {} ) {

    this._viewToTextureScale = null;

    this.addEvents(
      'loading',
      'loaded'
    );

    this.initSelection(config);

    this._initTextureView(config);

    this._readyDeferred = new Deferred();

    this._setDoc( new Document() );

    this.callParent(config);
  },

  ready$() {
    if (this._readyDeferred == null)
      this._readyDeferred = new Deferred();
    return this._readyDeferred.promise;
  },

  onObject3DChange() {
    // console.log('object 3D change');
    this.redraw('3D');
  },

  draw$() {

    return this.ready$() .then( () => {
    return this.object3D.ready$() .then( () => {
    return this.textureView.draw$() .then( () => {
    this.draw();
    });});});
  },

  optimizePreview() {
    this.textureView.optimizePreview();
  },
  optimizeZoomPreview() {
    if( this.updateTextureOnZoom ) {
      this.textureView.optimizePreview();
    }
  },

  _initTextureView( config = {} ) {

    let textureView = this._textureView;
    if( ! textureView ) {
      textureView = this._ownedTexture = new TextureView({
        canvas3D: this,
        selection: this.selection,
        autoDraw: false
      });

      // We need the final width to be a power of 2 so
      // Three.js can use THREE.LinearMipMapLinearFilter
      // Accept forced URL param
      textureView.textureSize = Config.textureSize3D ? parseInt(Config.textureSize3D) :
                                config.textureSize ? config.textureSize
                                                   : 2048;
    }
    else {
      textureView.canvas3D = this;
    }
    this._setTextureView(textureView);
  },

  syncModelData3D() {

    // console.log('syncModelData3D');

    this.object3D.un(this);
    this.object3D.dispose();

    const { modelData } = this;
    if( modelData ) {

      this.object3D = new Object3D({
        geometry: parseGeometry( modelData.data ),
        textureView: this.textureView,
        threeScene: this.threeScene,
      });
      this.object3D.on('change',this.onObject3DChange,this);

      this._viewToTextureScale = null;

      // Update camera and light position

      this.zoomToFront();
    }
    else {
      this.object3D = new EmptyObject3D();
    }
  },

  on_model3dchanged() {
    this.syncModelData3D();
    this.callParent(...arguments);
  },

  on_modeldata3dchanged() {
    this.syncModelData3D();
    this.callParent(...arguments);
  },


  _setTextureView( textureView ) {

    // Unlink old texture
    const old = this.textureView;
    if( old ) {
      this._unlinkTextureView(old);
    }

    // Link new one
    this._textureView = textureView;

    if( this.object3D ) {
      this.object3D.textureView = textureView;
      this.redraw('3D');
    }

    textureView.on({
      change : this.textureView_on_change.bind(this),
      draw   : this.textureView_on_draw.bind(this),
      redraw : this.textureView_on_redraw.bind(this),
    });
  },
  _unlinkTextureView( textureView ) {
    textureView.un(this);
    const { _ownedTexture } = this;
    if( _ownedTexture ) {
      _ownedTexture.dispose();
      this._ownedTexture = null;
    }
  },
  textureView_on_change( textureView, ...params ) {
    this.fireEvent('change',this,...params);
  },
  textureView_on_draw( textureView, ...params ) {
    this.fireEvent('draw',this,...params);
  },
  textureView_on_redraw( textureView, ...params ) {
    this.fireEvent('redraw',this,...params);
  },


  _setDoc( doc ) {

    const old = this.doc;

    if( old === doc ) {
      return resolve(); // Nothing to do
    }
    if( old ) {
      this.unlinkDoc(old);
    }
    this._document = doc;
    this.linkDoc(doc);

    const { textureView } = this;

    // Clear the current object3D
    if( this.object3D ) {
      this.object3D.un(this);
      this.object3D.dispose();
      this.object3D = new EmptyObject3D();
      this.redraw('3D');
    }

    if (Config.keepLowerLeftCornerAsOrigin) {
      this.center = this.viewCenter;
    }

    return textureView._setDoc( doc ) .then( () => {

    if( ! doc.is3D ) {
      return;
    }

    if (this._readyDeferred == null)
      this._readyDeferred = new Deferred();

    return doc.loadModelData3D$() .then( () => {

    this.syncModelData3D();

    this.onLoaded();

    }); });
  },

  dispose() {
    if( this.textureView ) {
      this._unlinkTextureView(this.textureView);
    }
    this.callParent();
  },

  pluginDirectDomEventHandling({ target }) {
    return target === 'view';
  },

  createEventTracker( event, docIsland ) {
    return new Tracker3D(event,docIsland);
  }
});


export const Canvas3D = StaticCanvas3D.extend( InteractiveView_Mixin ).extend({

  typeName: 'Canvas3D',

  Properties: {

  },

});



/** WEBPACK FOOTER **
 ** ./src/ui/canvas3d/canvas3d.js
 **/